import {
  GeneratedImageDescription,
  generateImageDescription,
  ImageDescriptionResponse,
} from '@/api/chatRelay';
import { tryJsonParse } from '@/helpers/jsonUtils';
import { ActionContext } from 'vuex';

export interface ChatRelayState {
  connectionId: string | undefined;
  descriptions: { [key: string]: GeneratedImageDescription | undefined };
}

const getInitialState = (): ChatRelayState => ({
  connectionId: undefined,
  descriptions: {},
});

const MIN_DESCRIPTIONS = 3;
const getters = {
  getAreImagesProcessed: (state: ChatRelayState): boolean =>
    Object.values(state.descriptions).every(Boolean) ||
    Object.values(state.descriptions).filter(Boolean).length >= MIN_DESCRIPTIONS,
};

const mutations = {
  setConnectionId(state: ChatRelayState, connectionId: string): void {
    state.connectionId = connectionId;
  },
  setImageDescription(
    state: ChatRelayState,
    {
      imageUrl,
      valueToSet,
    }: { imageUrl: string; valueToSet: GeneratedImageDescription | undefined },
  ): void {
    state.descriptions[imageUrl] = valueToSet;
  },
  removeImageDescription(state: ChatRelayState, url: string): void {
    delete state.descriptions[url];
  },
  clearImageDescriptions(state: ChatRelayState): void {
    state.descriptions = {};
  },
};

const actions = {
  async processImage(
    context: ActionContext<ChatRelayState, any>,
    imageUrl: string,
  ): Promise<GeneratedImageDescription | undefined> {
    context.commit('setImageDescription', { imageUrl, valueToSet: undefined });
    const response = await generateImageDescription(context.state.connectionId!, [imageUrl]);
    const generatedImageDescription = mapToDescription(response).shift() || { imageUrl };
    context.commit('setImageDescription', { imageUrl, valueToSet: generatedImageDescription });
    return generatedImageDescription;
  },
};

const mapToDescription = (response: ImageDescriptionResponse): GeneratedImageDescription[] => {
  if (response?.hasError || !response?.imageDescriptions) return [];

  return Object.entries(response.imageDescriptions).map(([key, value]) => ({
    imageUrl: key,
    features: tryJsonParse(value),
  }));
};

const _default = { state: getInitialState, getters, mutations, actions };
export default _default;
